import { useEffect, useState } from 'react';
import { DialogContent, Grid, Paper, Stack, Typography } from '@mui/material';
import { ReceiptLongOutlined } from '@mui/icons-material';
import { faCalendarAlt, faFilePdf, faHashtag, faMoneyBill, faStore, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  MolePrimaryButton,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { Util } from '../../util/Util';
import { Z_INDEX } from '../../styles/zIndex';
import type { ExpenseRow } from './expenseTypes';
import { expenseCategoryLabel, expenseNumber } from './expenseTypes';
import { openPdfFromBase64 } from '../voucher/voucherExport';
import { canExportPdf } from '../../util/access';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  bundle: Pick<ExpenseRow, 'id'> & Partial<ExpenseRow>;
};

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value?: string | null }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <FontAwesomeIcon icon={icon} style={{ width: 14, color: '#64748b' }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
    <Typography variant="body2" fontWeight={600}>{value || '—'}</Typography>
  </Stack>
);

const ExpenseReport = ({ isOpen, toggle, bundle }: Props) => {
  const { api, access } = useAuth();
  const { setLoadingMessage, queueReRun, t } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const [record, setRecord] = useState<ExpenseRow | null>(null);
  const currency = appSettings?.BIZ_CURRENCY?.value || 'USD';

  const categoryLabel = (value?: string) => expenseCategoryLabel(String(value || ''), t) || '—';

  const fetchData = async () => {
    if (!bundle?.id) return;
    const res = await api.get(`/expenses/${bundle.id}`);
    if (res?.status === 200) {
      setRecord(res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else if (res?.status === 404) {
      toast.warn(t('common:toast.warn.noRecord'));
      toggle();
    }
  };

  useEffect(() => {
    if (isOpen && bundle?.id) {
      void fetchData();
    }
    return () => {
      if (!isOpen) setRecord(null);
    };
  }, [isOpen, bundle?.id]);

  const handleExport = async () => {
    if (!bundle?.id || !canExportPdf(access, 'expense')) return;
    setLoadingMessage(t('common:report.loadingReport'));
    const res = await api.get(`/reports/expenses/${bundle.id}`);
    setLoadingMessage(null);
    if (res?.status === 200 && res.data?.data) {
      openPdfFromBase64(res.data.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleExport);
    } else {
      toast.error(t('common:report.failedToLoadReport'));
    }
  };

  const row = record || bundle;

  return (
    <MoleDialog
      open={isOpen}
      onClose={toggle}
      maxWidth="md"
      fullWidth
      themeColor={themeColor}
      paperOptions={{ maxHeight: '95vh' }}
      sx={{ zIndex: Z_INDEX.nestedDialog }}
    >
      <MoleDialogHeader
        title={t('common:expense.reportTitleNumbered', { number: expenseNumber(row) })}
        icon={<ReceiptLongOutlined />}
        onClose={toggle}
        themeColor={themeColor}
      />
      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              {t('common:expense.reportTitle')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
              <Stack spacing={1.5}>
                <DetailItem icon={faMoneyBill} label={t('common:tables.amount')} value={Util.formatCurrency(Number(row.amount || 0), currency)} />
                <DetailItem icon={faHashtag} label={t('common:expense.category')} value={categoryLabel(row.category)} />
                <DetailItem icon={faUser} label={t('common:expense.payee')} value={row.payee} />
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              {t('common:tables.details')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
              <Stack spacing={1.5}>
                <DetailItem icon={faCalendarAlt} label={t('common:tables.date')} value={row.date ? Util.formatDate(row.date, 'full') : '—'} />
                <DetailItem icon={faMoneyBill} label={t('common:tables.mode')} value={row.mode_name} />
                <DetailItem icon={faStore} label={t('common:tables.depot')} value={row.depot_name} />
                <DetailItem icon={faUser} label={t('common:tables.user')} value={row.user} />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        {row.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {row.description}
          </Typography>
        ) : null}
      </DialogContent>
      <MoleDialogActions>
        <MoleCancelButton onClick={toggle}>{t('common:ui.buttons.close')}</MoleCancelButton>
        {canExportPdf(access, 'expense') && (
          <MolePrimaryButton startIcon={<FontAwesomeIcon icon={faFilePdf} />} onClick={handleExport}>
            {t('common:ui.buttons.exportAsPdf')}
          </MolePrimaryButton>
        )}
      </MoleDialogActions>
    </MoleDialog>
  );
};

export default ExpenseReport;
